import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'react-hot-toast';
import { supabase } from '@/api/base44Client';
import { useClientSession } from '@/lib/ClientSessionContext';

export default function ClientOnboarding() {
  const navigate = useNavigate();
  const { clientUser, refreshClient } = useClientSession();

  const [brandName, setBrandName] = useState(
    clientUser?.brand_name && clientUser.brand_name !== 'Mitra Baru' ? clientUser.brand_name : ''
  );
  const [phone, setPhone] = useState(clientUser?.phone || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!brandName || !phone) {
      toast.error('Harap isi Nama Brand dan Nomor Telepon Anda.');
      return;
    }

    if (!password || password.length < 5) {
      toast.error('Kata sandi akun minimal 5 karakter!');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Konfirmasi kata sandi tidak cocok!');
      return;
    }

    if (!clientUser?.id) {
      toast.error('Sesi tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsSubmitting(true);
    toast.loading('Menyimpan data profil & kata sandi...', { id: 'onboarding' });

    try {
      // Get current row data
      const { data: row, error: getErr } = await supabase
        .from('ClientAccount')
        .select('*')
        .eq('id', clientUser.id)
        .single();
        
      if (getErr) throw getErr;

      // Merge new data
      const updatedData = { 
        ...row.data, 
        brand_name: brandName,
        phone: phone,
        password: password,
        company_name: brandName,
      };

      // Update Database
      const { error: updateErr } = await supabase
        .from('ClientAccount')
        .update({
          data: updatedData,
          updated_date: new Date().toISOString()
        })
        .eq('id', clientUser.id);

      if (updateErr) throw updateErr;

      // Refresh Session Context
      await refreshClient();

      toast.success('Profil & Kata Sandi Akun berhasil dibuat!', { id: 'onboarding' });
      navigate('/profil-saya');
      
    } catch (error) {
      console.error(error);
      toast.error('Gagal menyimpan profil: ' + error.message, { id: 'onboarding' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 items-center justify-center p-4">
      <div className="w-full max-w-md bg-white p-8 rounded-3xl shadow-xl border border-slate-100 font-sans">
        
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 bg-[#F9F5EC] text-[#8C6208] rounded-full flex items-center justify-center shadow-sm border border-[#E6D5B8]">
            <Sparkles className="w-6 h-6" />
          </div>
        </div>

        <div className="text-center mb-8">
          <h2 className="text-2xl font-black text-[#302621]">Lengkapi Akun Baru</h2>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            Silakan buat Kata Sandi akun Anda & lengkapi data brand untuk kemudahan akses portal.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-600 block">Nama Brand Kosmetik Anda</label>
            <Input 
              value={brandName}
              onChange={e => setBrandName(e.target.value)}
              placeholder="Contoh: Cielmora Skincare"
              className="h-11 border-slate-300 focus-visible:ring-[#8C6208] text-xs font-bold"
              required 
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-600 block">Nomor Telepon / WhatsApp Aktif</label>
            <Input 
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder="Contoh: 08123456789"
              className="h-11 border-slate-300 focus-visible:ring-[#8C6208] text-xs font-mono font-bold"
              required 
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-600 block">Buat Kata Sandi Akun</label>
            <Input 
              type={showPass ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Minimal 5 karakter"
              className="h-11 border-slate-300 focus-visible:ring-[#8C6208] text-xs"
              required 
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-bold text-slate-600 block">Konfirmasi Kata Sandi Baru</label>
            <Input 
              type={showPass ? "text" : "password"}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Ulangi kata sandi"
              className="h-11 border-slate-300 focus-visible:ring-[#8C6208] text-xs"
              required 
            />
          </div>

          <button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-[#302621] hover:bg-[#1A1411] text-white font-black h-12 rounded-xl transition-all shadow-md mt-6 flex justify-center items-center gap-2 disabled:opacity-70 text-xs"
          >
            {isSubmitting ? 'Memproses...' : 'Simpan & Masuk Portal'} <ArrowRight className="w-4 h-4" />
          </button>
        </form>

      </div>
    </div>
  );
}
